import React from 'react';
import { motion } from 'framer-motion';
import { Target, Users, Award, Zap, Wrench, Car, Paintbrush, Settings } from 'lucide-react';

const founders = [
  {
    name: 'Avinash Reddy Gangasani',
    role: 'Co-Founder',
    image: '/images/founder-avinash.png',
  },
  {
    name: 'Aman Hussain',
    role: 'Co-Founder',
    image: '/images/founder-aman.png',
  },
  {
    name: 'Preetam Reddy',
    role: 'Co-Founder',
    image: '/images/founder-preetam.png',
  },
];

const values = [
  {
    icon: Target,
    title: 'Precision',
    description: 'Every detail matters. We obsess over perfection in every vehicle we touch.',
  },
  {
    icon: Award,
    title: 'Quality',
    description: 'Using only premium products and techniques for lasting results.',
  },
  {
    icon: Zap,
    title: 'Performance',
    description: 'Enhancing both aesthetics and performance to exceed expectations.',
  },
  {
    icon: Users,
    title: 'Trust',
    description: 'Building lasting relationships through transparency and excellence.',
  },
];

const journey = [
  {
    year: '2019',
    title: 'The Beginning',
    description: 'Studio 44 Customs founded with a vision to bring premium automotive care to Hyderabad.',
  },
  {
    year: '2020',
    title: 'First Major Build',
    description: 'Completed our first Fortuner to Legender conversion, establishing our customization expertise.',
  },
  {
    year: '2021',
    title: 'Growing Team',
    description: 'Expanded to 15+ team members and opened our state-of-the-art facility.',
  },
  {
    year: '2022',
    title: '500+ Vehicles',
    description: 'Reached milestone of transforming 500+ vehicles with 100% customer satisfaction.',
  },
  {
    year: '2023',
    title: 'Doorstep Service',
    description: 'Launched premium doorstep detailing to serve clients at their convenience.',
  },
];

const expertise = [
  {
    icon: Car,
    title: 'Vehicle Customization',
    description: 'Body kits, conversions, and complete vehicle transformations',
  },
  {
    icon: Paintbrush,
    title: 'Paint Protection',
    description: 'PPF, ceramic coating, and paint correction services',
  },
  {
    icon: Wrench,
    title: 'Detailing Specialists',
    description: 'Interior and exterior detailing with premium products',
  },
  {
    icon: Settings,
    title: 'Performance Upgrades',
    description: 'ECU tuning, suspension, and turbo upgrades',
  },
];

const trustedBrands = [
  '3M', 'Ceramic Pro', 'Gtechniq', 'XPEL', 'Llumar', 'Avery Dennison', 'Oracal', 'V-KOOL',
];

const AboutPage = () => {
  return (
    <div className="pt-24 pb-20 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="text-primary uppercase tracking-[0.3em] text-sm font-medium mb-4 block">
            Who We Are
          </span>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2">
            About Studio 44<span className="text-primary">.</span>
          </h1>
          <p className="text-primary uppercase tracking-[0.2em] text-lg font-medium">
            Luxury car workshop
          </p>
        </motion.div>

        {/* Our Story */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-[4/3] rounded-2xl overflow-hidden image-zoom">
              <img
                src="/images/aboutsection.png"
                alt="Luxury car workshop"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-primary text-black p-6 rounded-2xl hidden md:block">
              <p className="font-display text-4xl font-bold">5+</p>
              <p className="text-sm font-medium">Years Experience</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
              Redefining Automotive Excellence
            </h2>
            <p className="text-gray-400 leading-relaxed mb-6">
              Founded by Avinash Reddy Gangasani, Aman Hussain, and Preetam Reddy,
              Studio 44 Customs was born from a shared passion for automotive perfection.
              We recognized a gap in the market for truly premium automotive care services
              in Hyderabad.
            </p>
            <p className="text-gray-400 leading-relaxed mb-8">
              Our mission is simple: bring luxury automotive standards to every vehicle
              we work on. Whether it is a daily driver or a supercar, we treat each
              project with the same dedication to excellence and attention to detail.
            </p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-8">
              <div>
                <p className="font-display text-3xl font-bold text-primary">500+</p>
                <p className="text-gray-400 text-sm">Vehicles Transformed</p>
              </div>
              <div>
                <p className="font-display text-3xl font-bold text-primary">50+</p>
                <p className="text-gray-400 text-sm">Custom Builds</p>
              </div>
              <div className="col-span-2 lg:col-span-1">
                <p className="font-display text-3xl font-bold text-primary">100%</p>
                <p className="text-gray-400 text-sm">Satisfaction Rate</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Mission & Vision */}
        <div className="grid md:grid-cols-2 gap-8 mb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-8"
          >
            <h3 className="text-2xl font-display font-bold text-white mb-4">Our Mission</h3>
            <p className="text-gray-400 leading-relaxed">
              To deliver premium automotive detailing and customization services that exceed
              customer expectations through precision, innovation, and unwavering commitment
              to quality. We aim to bring luxury automotive standards to every vehicle we touch.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="glass-card p-8"
          >
            <h3 className="text-2xl font-display font-bold text-white mb-4">Our Vision</h3>
            <p className="text-gray-400 leading-relaxed">
              To become the most trusted name in automotive transformation across India,
              known for our craftsmanship, attention to detail, and ability to turn
              automotive dreams into reality.
            </p>
          </motion.div>
        </div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-24"
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Our Core Values
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card p-8 hover-lift group"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-3">{value.title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Our Journey */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-24"
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Our Journey
          </h2>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-gradient-to-b from-primary via-primary/50 to-transparent hidden md:block" />
            <div className="space-y-12">
              {journey.map((item, index) => (
                <motion.div
                  key={item.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex flex-col md:flex-row items-center gap-8"
                >
                  <div className="flex-1 text-center md:text-right">
                    {index % 2 === 0 && (
                      <>
                        <h3 className="text-2xl font-display font-bold text-primary">{item.year}</h3>
                        <h4 className="text-xl font-semibold text-white mt-1">{item.title}</h4>
                        <p className="text-gray-400 mt-2">{item.description}</p>
                      </>
                    )}
                  </div>
                  <div className="w-4 h-4 rounded-full bg-primary border-4 border-black z-10 flex-shrink-0" />
                  <div className="flex-1 text-center md:text-left">
                    {index % 2 === 1 && (
                      <>
                        <h3 className="text-2xl font-display font-bold text-primary">{item.year}</h3>
                        <h4 className="text-xl font-semibold text-white mt-1">{item.title}</h4>
                        <p className="text-gray-400 mt-2">{item.description}</p>
                      </>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Our Expertise */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-24"
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white text-center mb-12">
            Our Expertise
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {expertise.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card p-8 text-center hover-lift group"
              >
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-primary/20 transition-colors">
                  <item.icon className="w-8 h-8 text-primary" />
                </div>
                <h4 className="text-xl font-semibold text-white mb-3">{item.title}</h4>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Trusted Brands */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-24"
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Brands We Trust
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            We use only premium products from industry-leading brands
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {trustedBrands.map((brand, index) => (
              <motion.div
                key={brand}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="glass-card px-8 py-4 hover-lift"
              >
                <span className="text-white font-semibold text-lg">{brand}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Founders */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white text-center mb-4">
            Meet the Founders
          </h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Three visionaries united by their passion for automotive excellence
          </p>
          <div className="grid sm:grid-cols-3 gap-8">
            {founders.map((founder, index) => (
              <motion.div
                key={founder.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="text-center group"
              >
                <div className="relative mb-6 inline-block">
                  <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-primary/20 group-hover:border-primary transition-colors duration-500">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>
                <h4 className="text-xl font-semibold text-white mb-1">{founder.name}</h4>
                <p className="text-primary text-sm">{founder.role}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AboutPage;
